import React, { useState } from 'react';
import { Card, Tag, Typography } from 'antd';
import type { WinImpactAnalysis, WinImpactWaterfall } from '../types/analyticsExplanation';
import type { ModelSeasonWaterfalls } from '../types/analyticsModel';
import './WinImpactWaterfalls.css';

const { Title, Paragraph, Text } = Typography;

const WIDTH = 880;
const LABEL_WIDTH = 220;
const PLOT_WIDTH = 610;
const ROW_HEIGHT = 62;
const TOP = 28;
const BOTTOM = 46;

const signed = (value: number, decimals = 1) =>
  `${value > 0 ? '+' : ''}${value.toFixed(decimals)}`;

interface ChartStep {
  key: string;
  label: string;
  value: number;
  start: number;
  end: number;
  kind: 'baseline' | 'positive' | 'negative' | 'neutral' | 'output';
  detail: string;
}

const buildSteps = (waterfall: WinImpactWaterfall): ChartStep[] => {
  const centered = waterfall.centerBaseline === true;
  const baselineGeometry = centered ? 0 : waterfall.baselineValue;
  let running = baselineGeometry;
  const contributionSteps = waterfall.contributions.map(contribution => {
    const start = running;
    running += contribution.value;
    return {
      key: contribution.key,
      label: contribution.label,
      value: contribution.value,
      start,
      end: running,
      kind: contribution.direction,
      detail: `${contribution.detail} · ${contribution.confidence} confidence`,
    } as ChartStep;
  });

  return [
    {
      key: 'baseline',
      label: waterfall.baselineLabel,
      value: waterfall.baselineValue,
      start: 0,
      end: baselineGeometry,
      kind: 'baseline',
      detail: centered ? 'Zero reference; label shows the fitted league baseline' : 'Starting value',
    },
    ...contributionSteps,
    {
      key: 'output',
      label: waterfall.outputLabel,
      value: waterfall.outputValue,
      start: 0,
      end: centered ? waterfall.outputValue - waterfall.baselineValue : waterfall.outputValue,
      kind: 'output',
      detail: 'Assessed output',
    },
  ];
};

const WaterfallChart: React.FC<{ waterfall: WinImpactWaterfall }> = ({ waterfall }) => {
  const steps = buildSteps(waterfall);
  const centered = waterfall.centerBaseline === true;
  const values = steps.flatMap(step => [step.start, step.end, 0]);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const minimumPadding = waterfall.unit === 'percentage points' ? 1 : 0.15;
  const padding = Math.max(minimumPadding, (rawMax - rawMin) * 0.08);
  const domainMin = rawMin - padding;
  const domainMax = rawMax + padding;
  const scale = (value: number) => LABEL_WIDTH
    + ((value - domainMin) / Math.max(1, domainMax - domainMin)) * PLOT_WIDTH;
  const chartHeight = TOP + steps.length * ROW_HEIGHT + BOTTOM;
  const tickCount = 5;
  const ticks = Array.from({ length: tickCount }, (_, index) =>
    domainMin + ((domainMax - domainMin) * index) / (tickCount - 1));

  return (
    <Card className="win-waterfall-card" bordered={false}>
      <div className="win-waterfall-heading">
        <div>
          <Title level={3}>{waterfall.title}</Title>
          <Paragraph>{waterfall.description}</Paragraph>
          {centered && <Text className="win-waterfall-zero-note">
            Chart axis shows impact from baseline; labels retain fitted values.
          </Text>}
        </div>
        <Tag>{centered ? `${waterfall.unit} vs baseline` : waterfall.unit}</Tag>
      </div>
      <div className="win-waterfall-scroll">
        <svg
          className="win-waterfall-svg"
          viewBox={`0 0 ${WIDTH} ${chartHeight}`}
          role="img"
          aria-label={`${waterfall.title}: ${waterfall.baselineValue.toFixed(1)} to ${waterfall.outputValue.toFixed(1)} ${waterfall.unit}`}
        >
          <title>{waterfall.title}</title>
          <g className="win-waterfall-axis-grid">
            {ticks.map(tick => (
              <g key={tick}>
                <line x1={scale(tick)} x2={scale(tick)} y1={TOP - 8} y2={chartHeight - BOTTOM + 7} />
                <text x={scale(tick)} y={chartHeight - 14} textAnchor="middle">{centered ? signed(tick) : tick.toFixed(1)}</text>
              </g>
            ))}
          </g>
          {steps.map((step, index) => {
            const y = TOP + index * ROW_HEIGHT;
            const x1 = scale(Math.min(step.start, step.end));
            const x2 = scale(Math.max(step.start, step.end));
            const width = Math.max(3, x2 - x1);
            const isTotal = step.kind === 'baseline' || step.kind === 'output';
            const valueLabel = isTotal
              ? `${step.value.toFixed(1)} ${waterfall.unit}`
              : `${signed(step.value)} ${waterfall.unit}`;
            const next = steps[index + 1];
            const geometryDelta = step.end - step.start;

            return (
              <g key={step.key} className={`win-waterfall-step win-waterfall-step--${step.kind}`}>
                <text className="win-waterfall-label" x={LABEL_WIDTH - 16} y={y + 25} textAnchor="end">
                  {step.label}
                </text>
                <title>{step.label}: {valueLabel}. {step.detail}</title>
                <rect x={x1} y={y + 6} width={width} height={29} rx={5} />
                <text
                  className="win-waterfall-value"
                  x={geometryDelta < 0 ? x1 - 8 : x2 + 8}
                  y={y + 25}
                  textAnchor={geometryDelta < 0 ? 'end' : 'start'}
                >
                  {valueLabel}
                </text>
                {next && step.kind !== 'output' && (
                  <line
                    className="win-waterfall-connector"
                    x1={scale(step.end)}
                    x2={scale(step.end)}
                    y1={y + 35}
                    y2={y + ROW_HEIGHT + 6}
                  />
                )}
              </g>
            );
          })}
          <line
            className="win-waterfall-zero"
            x1={scale(0)}
            x2={scale(0)}
            y1={TOP - 8}
            y2={chartHeight - BOTTOM + 7}
          />
        </svg>
      </div>
    </Card>
  );
};

const WinImpactWaterfalls: React.FC<{
  analysis: WinImpactAnalysis;
  heading?: string;
  seasonWaterfalls?: ModelSeasonWaterfalls[];
}> = ({ analysis, heading = 'Scenario impact waterfalls', seasonWaterfalls = [] }) => {
  const availableSeasons = [...seasonWaterfalls].sort((a, b) => b.year - a.year);
  const [selectedYear, setSelectedYear] = useState(availableSeasons[0]?.year);
  const selectedSeason = availableSeasons.find(season => season.year === selectedYear)
    ?? availableSeasons[0];
  const waterfalls = selectedSeason?.waterfalls ?? analysis.waterfalls;

  return (
    <section className="analytics-section win-impact-section">
      <div className="analytics-section-heading">
        <div>
          <Title level={2}>{heading}</Title>
          <Paragraph>
            {availableSeasons.length
              ? 'Choose one season to see its scoring, win-total, and playoff SHAP contributions.'
              : 'Signed contributions show how each explicit baseline reaches its assessed output.'}
          </Paragraph>
        </div>
          {availableSeasons.length > 0 && <Text className="win-impact-scope-note">
            Scoring quality is roster-based: opponent schedule is excluded from the PPG model
            and appears only in win-total and playoff models.
          </Text>}
        <div className="win-impact-controls">
          <Tag color="gold">{analysis.confidence}</Tag>
          {availableSeasons.length > 0 && (
            <label>
              <span>Season</span>
              <select
                aria-label="Model SHAP season"
                value={selectedSeason?.year}
                onChange={event => setSelectedYear(Number(event.target.value))}
              >
                {availableSeasons.map(season => (
                  <option value={season.year} key={season.year}>{season.year}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      </div>
      <div className="win-waterfall-legend" aria-label="Waterfall legend">
        <span><i className="is-baseline" /> Baseline / zero reference</span>
        <span><i className="is-positive" /> Positive impact</span>
        <span><i className="is-negative" /> Negative impact</span>
        <span><i className="is-output" /> Output</span>
      </div>
      <div className="win-waterfall-grid">
        {waterfalls.map(waterfall => (
          <WaterfallChart waterfall={waterfall} key={waterfall.key} />
        ))}
      </div>
      <Text className="win-impact-method">{analysis.method}</Text>
    </section>
  );
};

export default WinImpactWaterfalls;
